import { useCallback, useEffect, useRef } from "react";

import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { getOrganizationId } from "Libs/utils";
import { RootState } from "Store/configureStore";
import { useAppDispatch } from "Store/hooks";

import {
  selectCarbonEmissionsStateByOrganizationId,
  selectCarbonEmissionsIsLoadingByOrganizationId,
  selectCarbonEmissionsErrorByOrganizationId,
  selectCarbonEmissionsMonthlyDataForChart,
  selectCarbonEmissionsPreviousYearTotal,
  selectCarbonEmissionsProjectsTableData
} from "../carbonEmissions.selectors";
import { fetchPreviousYearMonthlyCarbonEmissionsThunk } from "../thunks/fetchOrganizationCarbonEmissions.thunk";

export const useOrganizationCarbonEmissions = (organizationName?: string) => {
  const dispatch = useAppDispatch();
  const dataFetchedRef = useRef(false);
  const { organizationId: urlOrganizationName } = useParams<{
    organizationId: string;
  }>();
  const effectiveOrgName = organizationName || urlOrganizationName;

  // Get the actual organizationId
  const organizationId = useSelector((state: RootState) =>
    getOrganizationId(state, effectiveOrgName)
  );

  // Create stable references for values that change
  const refs = useRef({
    orgId: organizationId
  });

  // Update refs when dependencies change
  useEffect(() => {
    refs.current = {
      orgId: organizationId
    };
  }, [organizationId]);

  // Select the data using the organizationId
  const state = useSelector((state: RootState) =>
    organizationId
      ? selectCarbonEmissionsStateByOrganizationId(state, { organizationId })
      : undefined
  );

  const isLoading = useSelector((state: RootState) =>
    organizationId
      ? selectCarbonEmissionsIsLoadingByOrganizationId(state, {
          organizationId
        })
      : false
  );

  const error = useSelector((state: RootState) =>
    organizationId
      ? selectCarbonEmissionsErrorByOrganizationId(state, { organizationId })
      : null
  );

  const monthlyChartData = useSelector((state: RootState) =>
    organizationId
      ? selectCarbonEmissionsMonthlyDataForChart(state, { organizationId })
      : []
  );

  const previousYearTotal = useSelector((state: RootState) =>
    organizationId
      ? selectCarbonEmissionsPreviousYearTotal(state, { organizationId })
      : 0
  );

  const projectsTableData = useSelector((state: RootState) =>
    organizationId
      ? selectCarbonEmissionsProjectsTableData(state, { organizationId })
      : []
  );

  const loadData = useCallback(() => {
    const { orgId } = refs.current;

    if (orgId && !dataFetchedRef.current) {
      dataFetchedRef.current = true;

      return dispatch(fetchPreviousYearMonthlyCarbonEmissionsThunk(orgId));
    }
  }, [dispatch]);

  // Reset dataFetched flag if state data is null
  useEffect(() => {
    if (dataFetchedRef.current && !state?.data) {
      dataFetchedRef.current = false;
    }

    // Load data automatically if needed
    if (
      !isLoading &&
      !state?.data &&
      organizationId &&
      !dataFetchedRef.current
    ) {
      loadData();
    }
  }, [organizationId, state?.data, isLoading, loadData]);

  return {
    isLoading,
    error,
    loadData,
    monthlyChartData,
    previousYearTotal,
    projectsTableData
  };
};
